import { useState } from 'react';

import { useSVGContext } from '../../../../contexts/GraphicContext';
import { TFlat } from '../../../../shared/types/flat';
import { mapFlatStatusClass, mapFlatStatusText } from '../../../../shared/mappers/mappers';

import ContactForm from './lib/ContactForm/ContactForm';
import { ImagePopup } from './lib/ImagePopup/ImagePopup';

import styles from './FlatDetails.module.css';
import { useFlatsDataContext } from '../../../../contexts/FlatsDataContext';
import { paths } from '../../../../settings';

const FlatDetails: React.FC<{ flat: TFlat }> = ({ flat }): JSX.Element => {
    const { handleHideFlatDetailsPane } = useSVGContext();
    const { contextSelectedFloor } = useFlatsDataContext();

    const [showPopup, setShowPopup] = useState(false);

    /**
     * Returns the label based on the number of rooms in a Flat.
     * @returns {string} - The label for rooms.
    */
    const getRoomLabel = (): string => {
        switch (flat.rooms) {
            case 1:
                return 'pokój';
            case 2:
            case 3:
            case 4:
                return 'pokoje';
            default:
                return 'pokoi';
        }
    };

    /**
     * Renders a list of chips representing additional spaces available in the flat.
     * @returns {JSX.Element} - A JSX fragment.
    */
    const renderAdditionalSpaceChips = (): JSX.Element => (
        <>
            {flat.additionalSpace.map((space, index) => (
                <span key={index} className={styles.spaceChip}>
                    {space}
                </span>
            ))}
        </>
    );

    return (
        <div className={styles.wrapper}>
            <div className={styles.heading}>
                <div role="button" className={styles.backButton} onClick={handleHideFlatDetailsPane}>
                    <img src={`${paths.assets}/icons/arrow.png`} width={24} height={24} alt="<" />
                    <span>{ contextSelectedFloor ? 'Wróć do wyszukiwarki apartamentów' : 'Wróć do widoku budynku' }</span>
                </div>
                <div className={styles.headingRow}>
                    <div className={styles.headingRowTitle}>
                        <h1>Apartament</h1>
                        <h2>{flat.name.toUpperCase()}</h2>  
                    </div>
                    <div className={styles.contactIcons}>
                        <a href={flat.file} target="_blank" rel="noopener noreferrer">
                            <img src={`${paths.assets}/icons/download.png${paths.version}`} title="Pobierz" width={18} height={18} alt="Pobierz" />
                            <span>Pobierz kartę</span>
                        </a>
                    </div>
                </div>
                <p className={styles[mapFlatStatusClass(flat.status)]}>
                    { mapFlatStatusText(flat.status)}
                </p>
            </div>
            <div className={styles.detailsChips}>
                <div>
                    <span>{ flat.floor.name }</span>
                </div>
                <div>
                    <span>{flat.area}m<sup>2</sup></span>
                </div>
                <div>
                    {flat.rooms} { getRoomLabel() }
                </div>
            </div>
            <div className={styles.detailsSecondChips}>
                {renderAdditionalSpaceChips()}
            </div>
            <div className={styles.mainImageWrapper}>
                <img className={styles.zoomIcon} src={`${paths.assets}/icons/zoom.png`} alt="Zoom" onClick={() => setShowPopup(true)} />
                <img className={styles.mainImage} src={flat.image} alt="Apartament zdjęcie" onClick={() => setShowPopup(true)} />
            </div>
            <ImagePopup 
                isOpen={showPopup}
                imageUrl={flat.file_jpg}
                onClose={() => setShowPopup(false)}
            />
            <ContactForm />
        </div>
    );
};

export default FlatDetails;